import React from 'react';
import PropTypes from 'prop-types';
import css from '@src/styles/components/popup.scss';
import styles from '@src/js/Style';
import Scroll from '../../Scroll';
import AdaptiveTrait from '../../AdaptiveTrait';

const config = {
    stateActive: css['active'],
    startLayer: 10000,
};

export default class Popup extends React.Component {

    constructor(props) {

        super(props);

        // eslint-disable-next-line no-new
        new AdaptiveTrait(this);
    }

    static get propTypes() {

        return {
            title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
            isActive: PropTypes.bool,
            children: PropTypes.any,
            onShow: PropTypes.func,
            onClose: PropTypes.func,
        };
    }

    renderMobile() {

        return (
            <div ref={ (node) => { this.overlay = node; } } className={ css['popup__overlay'] }>
                <div ref={ (el) => this.handleShow(el) } className={ css['popup'] }>
                    <div ref={ (node) => { this.head = node; } } className={ css['popup__head'] }>
                        <div className={ css['popup__title'] }>
                            { this.props.title }
                        </div>
                        <div className={ css['popup__close'] } onClick={ () => this.hide() }>
                            <i className={ styles('icon-close') }></i>
                        </div>
                    </div>
                    { this.renderBody() }
                </div>
            </div>
        );
    }

    renderDesktop() {

        return this.renderMobile();
    }

    renderBody() {

        if (typeof (this.props.children) == 'string') {

            // raw html

            return <div
                className={ css['popup__body'] }
                dangerouslySetInnerHTML={{ __html: this.props.children }} />;
        }

        return (
            <div className={ css['popup__body'] } data-role="body">
                { this.props.children }
            </div>
        );
    }

    toggle() {

        // eslint-disable-next-line no-unused-expressions
        !this.overlay.classList.contains(config.stateActive)
            ? this.show()
            : this.hide();
    }

    show() {

        this.overlay.classList.add(config.stateActive);

        if (this.popup.layerIndex == undefined) {

            this.popup.layerIndex = Popup.getLayerIndex();
            this.overlay.style['z-index'] = config.startLayer + this.popup.layerIndex;
        }

        this.scroll.disable();
    }

    hide() {

        if (this.overlay.classList.contains(config.stateActive)) {

            this.overlay.classList.remove(config.stateActive);

            if (!Popup.hasActivePopups())
                this.scroll.enable();

            if (this.props.onClose != undefined)
                this.props.onClose(this);
        }
    }

    handleShow(el) {

        this.popup = el;

        if (this.props.onShow != undefined)
            this.props.onShow(this);
    }

    get body() {

        return this.popup;
    }

    componentDidMount() {

        this.scroll = new Scroll();

        /** hide by esc key */
        this.keyEventFunction = (e) => {

            if (e.key == 'Escape')
                this.hide();
        };
        window.addEventListener('keydown', this.keyEventFunction);

        /** hide by click on overlay */
        let sourceClickTarget = null;
        this.overlay.addEventListener('mousedown', (e) => {

            sourceClickTarget = e.target;
        });
        this.overlay.addEventListener('mouseup', (e) => {

            if (e.button == /* LEFT */ 0 && sourceClickTarget == this.overlay)
                this.hide();

            sourceClickTarget = null;
        });

        this.componentDidUpdate();

        /** show if active by default */
        if (this.props.isActive)
            this.show();
    }

    componentDidUpdate() {

        // eslint-disable-next-line no-unused-expressions
        AdaptiveTrait.isMobile()
            ? this.bindMobile()
            : this.bindDesktop();
    }

    componentWillUnmount() {

        if (this.keyEventFunction) {

            window.removeEventListener('keydown', this.keyEventFunction);
            this.keyEventFunction = null;
        }

        this.hide();
    }

    static getActivePopupSelector() {

        return `.${css['popup__overlay']}.${config.stateActive} .${css['popup']}`;
    }

    static hasActivePopups() {

        return document.querySelectorAll(Popup.getActivePopupSelector()).length > 0;
    }

    static getLayerIndex() {

        let max = 0;
        document
            .querySelectorAll(Popup.getActivePopupSelector())
            .forEach((el) => {

                if (el.layerIndex > max)
                    max = el.layerIndex;
            });

        return ++max;
    }

    bindMobile() {

        let
            startPosition = 0,
            started = false;

        const getClientY = (e) => {

            if (e.clientY != undefined)
                return e.clientY;

            if (e.touches[0] != undefined)
                return e.touches[0].clientY;

            return e.changedTouches[0].clientY;
        };

        if (!this.isBindMobile) {

            this.touchStartEvent = (e) => {

                startPosition = getClientY(e);
                started = true;
            };

            this.touchMoveEvent = (e) => {

                if (started) {

                    if (e.cancelable)
                        e.preventDefault();

                    let offset = getClientY(e) - startPosition;
                    if (offset < 0)
                        offset = 0;

                    this.popup.style.transform = `translateY(${offset}px)`;
                }
            };

            this.touchEndEvent = (e) => {

                if (started) {

                    if (e.cancelable)
                        e.preventDefault();

                    const
                        offset = getClientY(e) - startPosition,
                        percent = offset / (this.popup.offsetHeight / 100);

                    if (percent > 15)
                        this.hide();

                    this.popup.style.transform = null;
                    started = false;
                }
            };

            this.head.addEventListener('touchstart', (e) => this.touchStartEvent(e), { passive: true });
            this.head.addEventListener('mousedown', (e) => this.touchStartEvent(e));
            this.head.addEventListener('touchmove', (e) => this.touchMoveEvent(e), { passive: true });
            this.head.addEventListener('mousemove', (e) => this.touchMoveEvent(e));
            this.head.addEventListener('touchend', (e) => this.touchEndEvent(e));
            this.head.addEventListener('mouseup', (e) => this.touchEndEvent(e));

            this.isBindMobile = true;
        }

    }

    bindDesktop() {

        if (this.isBindMobile) {

            this.head.removeEventListener('touchstart', (e) => this.touchStartEvent(e), { passive: true });
            this.head.removeEventListener('mousedown', (e) => this.touchStartEvent(e));
            this.head.removeEventListener('touchmove', (e) => this.touchMoveEvent(e), { passive: true });
            this.head.removeEventListener('mousemove', (e) => this.touchMoveEvent(e));
            this.head.removeEventListener('touchend', (e) => this.touchEndEvent(e));
            this.head.removeEventListener('mouseup', (e) => this.touchEndEvent(e));
        }
    }

    getNativeElement() {

        return this.popup;
    }

}